import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { buildThemePalette, DEFAULT_THEME, SHOP_THEME_VARIABLE_KEYS } from '../utils/theme';

const SHOP_THEME_ROLES = ['SHOP_OWNER', 'SHOP_STAFF', 'SHOP_OPERATOR', 'CHEF', 'DELIVERY', 'BEARER', 'HOTEL_USER'];

const useShopTheme = () => {
  const profileTheme = useSelector((state) => state.shop?.profile?.theme);
  const role = useSelector((state) => state.auth?.user?.role);
  const location = useLocation();

  const isShopUser = SHOP_THEME_ROLES.includes(role);
  const isDashboardRoute = location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/shope-dashboard');
  const shouldApplyTheme = isShopUser && isDashboardRoute;

  const themeData = useMemo(() => {
    if (!shouldApplyTheme) {
      return buildThemePalette(DEFAULT_THEME);
    }
    return buildThemePalette(profileTheme || DEFAULT_THEME);
  }, [shouldApplyTheme, profileTheme]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const root = document.documentElement;
    const body = document.body;

    if (!shouldApplyTheme) {
      return undefined;
    }

    Object.entries(themeData.cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    body.classList.add('shop-theme-active');
    body.style.background = themeData.palette.background;
    body.style.backgroundColor = themeData.palette.background;
    body.style.backgroundImage = 'none';

    return () => {
      SHOP_THEME_VARIABLE_KEYS.forEach((key) => {
        root.style.removeProperty(key);
      });
      body.classList.remove('shop-theme-active');
      body.style.background = '';
      body.style.backgroundColor = '';
      body.style.backgroundImage = '';
    };
  }, [themeData, shouldApplyTheme]);

  return {
    palette: themeData.palette,
    cssVariables: themeData.cssVariables,
    isActive: shouldApplyTheme
  };
};

export default useShopTheme;
